/**
 * FLAGS — catálogo de features auto-descriptivo (admin.features), ahora con el
 * grafo de dependencias visible y toggles con consecuencias:
 *
 *  · chips `requires`/`conflicts` por feature + vista inversa «dependen de mí»;
 *  · ciclo de vida prominente (status + since) para decisiones de rollout;
 *  · docs por feature (rutas `/help/<slug>` internas → Link del router);
 *  · TODO descriptor con fila de flag es togglable (kill-switch de plataforma),
 *    siempre con diálogo de confirmación que muestra implicaciones + efectos
 *    de grafo; las violaciones DURAS bloquean el confirmar (el server las
 *    rechazaría con 409 y la cadena).
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, type JSX, Show } from "solid-js";
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  LayoutGrid,
  Link2,
  Search,
  ShieldCheck,
} from "lucide-solid";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { TextField } from "@/components/ui/text-field";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import {
  adminQueryKeys,
  type components,
  dependentsOf,
  type FeatureGraphNode,
  newViolations,
  parseRequireClause,
} from "@space/shared";
import { Link } from "@/lib/u-router";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";
import {
  FeatureToggleDialog,
  type FeatureToggleRequest,
} from "@/features/flags/feature-toggle-dialog";

type ErrorModel = components["schemas"]["ErrorModel"];
type ListResponse = components["schemas"]["FeaturesListResponse"];
type FeatureEntry = components["schemas"]["FeatureCatalogEntry"];

const CATEGORIES = [
  "vertical",
  "payment_method",
  "integration",
  "module_kind",
  "meeting_provider",
  "learning_block",
  "flag",
  "admin_tool",
  "io_entity",
  "country_pack",
  "grading_scheme",
] as const;
const SCOPES = ["platform", "org", "course"] as const;
const STATUSES = ["stable", "beta", "stub", "deprecated"] as const;

/**
 * Invierte el mapeo de `FlagsCatalogSource` (Rust): un flag sin punto se publica
 * como `flag.<key>`. Para escribir en la tabla `feature_flags` hay que recuperar
 * la key original.
 */
function flagKeyFromDescriptor(key: string): string {
  if (key.startsWith("flag.")) {
    const rest = key.slice("flag.".length);
    if (!rest.includes(".")) {
      return rest;
    }
  }
  return key;
}

/** snake_case → texto legible ("payment_method" → "payment method"). */
function humanize(s: string): string {
  return s.replaceAll("_", " ");
}

function hasFlagState(entry: FeatureEntry): boolean {
  return typeof entry.enabled === "boolean";
}

/** Grafo TS construido desde las entradas del API (fuente completa: Rust+TS). */
function entriesGraph(entries: FeatureEntry[]): Map<string, FeatureGraphNode> {
  const graph = new Map<string, FeatureGraphNode>();
  for (const e of entries) {
    graph.set(e.key, {
      key: e.key,
      name: e.name,
      scope: e.scope,
      requires: e.requires ?? [],
      conflicts: e.conflicts ?? [],
      // §L1: el catálogo del API sirve el estado REAL de cada flag, así que el
      // default de fábrica ya no decide nada aquí; `true` es solo el fondo.
      defaultEnabled: true,
    });
  }
  return graph;
}

export function FeatureCatalog() {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();
  const canToggleFlags = () => can("flags.write");

  const [search, setSearch] = createSignal("");
  const [category, setCategory] = createSignal<string | undefined>(undefined);
  const [scope, setScope] = createSignal<string | undefined>(undefined);
  const [status, setStatus] = createSignal<string | undefined>(undefined);
  const [expanded, setExpanded] = createSignal<string | null>(null);
  const [pendingToggle, setPendingToggle] = createSignal<
    (FeatureToggleRequest & { entry: FeatureEntry }) | null
  >(null);

  const filter = createMemo(() => ({
    search: search().trim() || undefined,
    category: category(),
    scope: scope(),
    status: status(),
  }));

  const query = createApiQuery<ListResponse, ErrorModel>(
    () => adminQueryKeys.features(filter()),
    async () => api.GET("/api/admin/features", { params: { query: filter() } }),
    () => ({ staleTime: 15_000 }),
  );
  // Catálogo COMPLETO sin filtros (grafo + vista inversa estables aunque se filtre).
  const fullQuery = createApiQuery<ListResponse, ErrorModel>(
    () => adminQueryKeys.features({}),
    async () => api.GET("/api/admin/features", { params: { query: {} } }),
    () => ({ staleTime: 15_000 }),
  );

  const graph = createMemo(() => entriesGraph(fullQuery.data?.data ?? []));
  const enabledByKey = createMemo(() => {
    const map = new Map<string, boolean>();
    for (const e of fullQuery.data?.data ?? []) {
      map.set(e.key, e.enabled ?? true);
    }
    return map;
  });

  const toggleMutation = createApiMutation(
    async (vars: components["schemas"]["UpsertFeatureFlagDto"]) =>
      api.POST("/api/admin/flags", { body: vars }),
  );

  // Agrupa por categoría preservando el orden global por key del backend.
  const grouped = createMemo(() => {
    const groups = new Map<string, FeatureEntry[]>();
    for (const entry of query.data?.data ?? []) {
      const list = groups.get(entry.category) ?? [];
      list.push(entry);
      groups.set(entry.category, list);
    }
    return [...groups.entries()];
  });

  /** Abre el diálogo de confirmación con implicaciones + efectos de grafo. */
  const requestToggle = (entry: FeatureEntry, enabled: boolean) => {
    const g = graph();
    const isOn = (k: string) => enabledByKey().get(k) ?? true;
    const nextOn = (k: string) => (k === entry.key ? enabled : isOn(k));
    const violations = newViolations(g, isOn, nextOn);
    const softAffected = enabled
      ? []
      : dependentsOf(entry.key, g)
          .filter((n) => isOn(n.key))
          .filter((n) =>
            n.requires.some((clause) =>
              parseRequireClause(clause).some((a) => a.key === entry.key && a.soft),
            ),
          )
          .map((n) => n.name);
    setPendingToggle({
      entry,
      key: entry.key,
      name: entry.name,
      nextEnabled: enabled,
      status: entry.status,
      since: entry.since ?? undefined,
      implications: entry.implications ?? undefined,
      violations,
      softAffected,
    });
  };

  const confirmToggle = () => {
    const pending = pendingToggle();
    if (!pending) {
      return;
    }
    setPendingToggle(null);
    toggleMutation.mutate(
      {
        key: flagKeyFromDescriptor(pending.entry.key),
        description: pending.entry.description,
        enabled: pending.nextEnabled,
      },
      {
        onSuccess: () => {
          showToast(t(msg`Updated`), "success");
          void query.refetch();
          void fullQuery.refetch();
        },
        onError: (err) => {
          // 409 con la cadena de dependencias en message.
          const message =
            typeof err === "object" && err !== null && "message" in err
              ? String((err as { message: unknown }).message)
              : t(msg`Update failed`);
          showToast(message, "error");
        },
      },
    );
  };

  const total = () => query.data?.data.length ?? 0;

  return (
    <div class="space-y-6">
      <div class="flex items-center gap-3">
        <LayoutGrid class="h-8 w-8 text-primary" />
        <div>
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Feature catalog`)}</h2>
          <p class="text-sm text-muted-foreground">
            {t(msg`Every capability, self-described. No hidden features.`)}
          </p>
        </div>
      </div>

      <div class="space-y-3">
        <div class="relative">
          <Search class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <TextField
            class="pl-9"
            placeholder={t(msg`Search key, name, description…`)}
            value={search()}
            onInput={(e) => {
              setSearch(e.currentTarget.value);
            }}
          />
        </div>

        <FilterChips
          label={t(msg`Category`)}
          options={CATEGORIES}
          value={category()}
          onChange={setCategory}
        />
        <FilterChips label={t(msg`Scope`)} options={SCOPES} value={scope()} onChange={setScope} />
        <FilterChips
          label={t(msg`Status`)}
          options={STATUSES}
          value={status()}
          onChange={setStatus}
        />
      </div>

      <Show
        when={!query.isLoading}
        fallback={
          <div class="space-y-3">
            <For each={Array.from({ length: 6 }, () => 0)}>
              {() => <Skeleton class="h-14 w-full rounded-xl" />}
            </For>
          </div>
        }
      >
        <Show
          when={total() > 0}
          fallback={
            <Card>
              <CardContent class="p-8 text-center text-sm text-muted-foreground">
                {t(msg`No features match the filters.`)}
              </CardContent>
            </Card>
          }
        >
          {(() => {
            const count = total();
            return <div class="text-xs text-muted-foreground">{t(msg`${count} features`)}</div>;
          })()}
          <div class="space-y-6">
            <For each={grouped()}>
              {([cat, entries]) => (
                <div class="space-y-2">
                  <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {humanize(cat)}
                  </h3>
                  <div class="space-y-2">
                    <For each={entries}>
                      {(entry) => (
                        <FeatureRow
                          entry={entry}
                          graph={graph()}
                          dependents={dependentsOf(entry.key, graph())}
                          expanded={expanded() === entry.key}
                          onToggleExpand={() => {
                            setExpanded(expanded() === entry.key ? null : entry.key);
                          }}
                          canToggle={canToggleFlags()}
                          onToggleFlag={(v) => {
                            requestToggle(entry, v);
                          }}
                        />
                      )}
                    </For>
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>

      <FeatureToggleDialog
        request={pendingToggle()}
        graph={graph()}
        onConfirm={confirmToggle}
        onCancel={() => setPendingToggle(null)}
      />
    </div>
  );
}

function FilterChips(props: {
  label: string;
  options: readonly string[];
  value: string | undefined;
  onChange: (v: string | undefined) => void;
}) {
  const { t } = useI18n();
  return (
    <div class="flex flex-wrap items-center gap-2">
      <span class="text-xs font-medium text-muted-foreground w-16 shrink-0">{props.label}</span>
      <Chip
        active={props.value === undefined}
        onClick={() => {
          props.onChange(undefined);
        }}
      >
        {t(msg`All`)}
      </Chip>
      <For each={props.options}>
        {(opt) => (
          <Chip
            active={props.value === opt}
            onClick={() => {
              props.onChange(props.value === opt ? undefined : opt);
            }}
          >
            {humanize(opt)}
          </Chip>
        )}
      </For>
    </div>
  );
}

function Chip(props: { active: boolean; onClick: () => void; children: JSX.Element }) {
  return (
    <button
      type="button"
      onClick={() => {
        props.onClick();
      }}
      class={`rounded-full border px-3 py-1 text-xs transition-colors ${
        props.active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-background hover:bg-muted"
      }`}
    >
      {props.children}
    </button>
  );
}

/** Chip de una key del grafo (nombre legible + key mono). */
function KeyChip(props: {
  keyName: string;
  graph: ReadonlyMap<string, FeatureGraphNode>;
  soft?: boolean;
}) {
  const { t } = useI18n();
  const label = () => props.graph.get(props.keyName)?.name ?? props.keyName;
  return (
    <span
      class="inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs"
      title={props.keyName}
    >
      <Link2 class="h-3 w-3 text-muted-foreground" />
      {label()}
      <Show when={props.soft}>
        <span class="text-muted-foreground">({t(msg`optional`)})</span>
      </Show>
    </span>
  );
}

function FeatureRow(props: {
  entry: FeatureEntry;
  graph: ReadonlyMap<string, FeatureGraphNode>;
  dependents: FeatureGraphNode[];
  expanded: boolean;
  onToggleExpand: () => void;
  canToggle: boolean;
  onToggleFlag: (v: boolean) => void;
}) {
  const { t } = useI18n();
  const schemaPretty = () =>
    props.entry.configSchema ? JSON.stringify(props.entry.configSchema, null, 2) : null;
  const requires = () => props.entry.requires ?? [];
  const conflicts = () => props.entry.conflicts ?? [];
  const statusVariant = () =>
    props.entry.status === "stable" ? ("default" as const) : ("secondary" as const);

  return (
    <Card>
      <CardContent class="p-0">
        <div class="flex w-full items-center gap-3 p-4">
          <button
            type="button"
            class="flex min-w-0 flex-1 items-center gap-3 text-left"
            onClick={() => {
              props.onToggleExpand();
            }}
          >
            <Show
              when={props.expanded}
              fallback={<ChevronRight class="h-4 w-4 shrink-0 text-muted-foreground" />}
            >
              <ChevronDown class="h-4 w-4 shrink-0 text-muted-foreground" />
            </Show>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <span class="font-medium truncate">{props.entry.name}</span>
                {/* FLAGS: ciclo de vida prominente — status + since. */}
                <Badge variant={statusVariant()} class="shrink-0">
                  {props.entry.status}
                </Badge>
                <Show when={props.entry.since}>
                  {(since) => (
                    <span class="shrink-0 text-[11px] text-muted-foreground">{since()}</span>
                  )}
                </Show>
                <Show when={props.entry.gateable}>
                  <span
                    class="inline-flex shrink-0 items-center gap-0.5 text-[11px] text-muted-foreground"
                    title={t(msg`Server-enforced: writes are blocked when disabled`)}
                  >
                    <ShieldCheck class="h-3 w-3 text-success" /> {t(msg`enforced`)}
                  </span>
                </Show>
              </div>
              <div class="font-mono text-xs text-muted-foreground truncate">{props.entry.key}</div>
            </div>
          </button>
          <Show
            when={hasFlagState(props.entry)}
            fallback={
              <span class="shrink-0 text-xs text-muted-foreground">{props.entry.scope}</span>
            }
          >
            <div class="shrink-0">
              <Switch
                checked={props.entry.enabled ?? false}
                disabled={!props.canToggle}
                onCheckedChange={props.onToggleFlag}
              />
            </div>
          </Show>
        </div>

        <Show when={props.expanded}>
          <div class="space-y-3 border-t px-4 py-4 pl-11">
            <p class="text-sm text-muted-foreground">{props.entry.description}</p>
            <div class="flex flex-wrap gap-2 text-xs">
              <Badge variant="outline">{humanize(props.entry.category)}</Badge>
              <Badge variant="outline">
                {t(msg`scope:`)} {props.entry.scope}
              </Badge>
            </div>

            {/* FLAGS: grafo — requiere / incompatible / dependen de mí. */}
            <Show when={requires().length > 0}>
              <div data-testid="feature-requires">
                <div class="mb-1 text-xs font-medium text-muted-foreground">{t(msg`Requires`)}</div>
                <div class="flex flex-wrap gap-1.5">
                  <For each={requires()}>
                    {(clause) => (
                      <span class="inline-flex flex-wrap items-center gap-1">
                        <For each={parseRequireClause(clause)}>
                          {(alt, i) => (
                            <>
                              <Show when={i() > 0}>
                                <span class="text-xs text-muted-foreground">{t(msg`or`)}</span>
                              </Show>
                              <KeyChip keyName={alt.key} graph={props.graph} soft={alt.soft} />
                            </>
                          )}
                        </For>
                      </span>
                    )}
                  </For>
                </div>
              </div>
            </Show>
            <Show when={conflicts().length > 0}>
              <div data-testid="feature-conflicts">
                <div class="mb-1 text-xs font-medium text-destructive">
                  {t(msg`Incompatible with`)}
                </div>
                <div class="flex flex-wrap gap-1.5">
                  <For each={conflicts()}>
                    {(key) => <KeyChip keyName={key} graph={props.graph} />}
                  </For>
                </div>
              </div>
            </Show>
            <Show when={props.dependents.length > 0}>
              <div data-testid="feature-dependents">
                <div class="mb-1 text-xs font-medium text-muted-foreground">
                  {t(msg`Depended on by`)}
                </div>
                <div class="flex flex-wrap gap-1.5">
                  <For each={props.dependents}>
                    {(dep) => <KeyChip keyName={dep.key} graph={props.graph} />}
                  </For>
                </div>
              </div>
            </Show>

            {/* FLAGS: implicaciones declaradas (qué pasa al apagarla). */}
            <Show when={props.entry.implications}>
              {(impl) => (
                <div class="rounded-lg border border-border/60 bg-muted/30 p-3 text-xs">
                  <Show when={impl().navHidden?.length}>
                    <p class="mb-1">
                      <span class="font-semibold">{t(msg`Disappears:`)}</span>{" "}
                      {impl().navHidden?.join(" · ")}
                    </p>
                  </Show>
                  <Show when={impl().flowsBlocked?.length}>
                    <p class="mb-1">
                      <span class="font-semibold">{t(msg`Blocks:`)}</span>{" "}
                      {impl().flowsBlocked?.join(" · ")}
                    </p>
                  </Show>
                  <p class="text-muted-foreground">
                    {t(msg`Data policy: preserved — nothing is ever deleted by a toggle.`)}
                  </p>
                </div>
              )}
            </Show>

            <Show when={props.entry.docsUrl}>
              {(url) => (
                <Show
                  when={url().startsWith("/")}
                  fallback={
                    <a
                      href={url()}
                      target="_blank"
                      rel="noreferrer"
                      class="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      {t(msg`Documentation`)}
                      <ExternalLink class="h-3 w-3" />
                    </a>
                  }
                >
                  <Link
                    to={url()}
                    class="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    data-testid="feature-docs-link"
                  >
                    {t(msg`Guide in the help center`)}
                    <ExternalLink class="h-3 w-3" />
                  </Link>
                </Show>
              )}
            </Show>
            <Show when={schemaPretty()}>
              {(pretty) => (
                <div>
                  <div class="mb-1 text-xs font-medium text-muted-foreground">
                    {t(msg`Config schema`)}
                  </div>
                  <pre class="overflow-x-auto rounded-lg bg-muted p-3 text-xs">{pretty()}</pre>
                </div>
              )}
            </Show>
          </div>
        </Show>
      </CardContent>
    </Card>
  );
}
